import { Capacitor } from "@capacitor/core"
import { ref } from "vue"
import type {
  BankAccountSelection,
  BankBootstrapResult,
  BankCandidateAccount,
  BankConnection,
  BankInstitution,
  BankMerchantMapping,
  BankOrigin,
  BankPlatform,
  BankSyncResult,
} from "../types/bank"
import { apiRequest } from "./apiClient"

// Bumped after a bank link is finalized so a mounted BankConnections list
// reloads (the finalize happens at app startup, possibly before the settings
// view is shown).
export const bankRefreshSignal = ref(0)
export function triggerBankRefresh() {
  bankRefreshSignal.value++
}

// Raised when the app returns from the bank with accounts awaiting selection
// (?bank=select or a successful /exchange), so BankConnections opens the
// account picker. A persistent flag rather than a counter: it's raised during
// app startup, usually BEFORE the settings view (and BankConnections) has
// mounted — a one-shot event would be missed. The consumer resets it.
export const bankSelectPending = ref(false)
export function triggerBankSelect() {
  bankSelectPending.value = true
}

// Client for the Open Banking (PSD2) routes. Connecting a bank is a redirect
// flow: connect() returns a hosted bank-login link, the browser navigates
// there, the bank redirects to our PUBLIC server callback
// (/api/bank/callback), which exchanges the code and runs the first import
// server-side, then bounces back to /settings?bank=linked.

class BankService {
  listInstitutions(country = "NO"): Promise<BankInstitution[]> {
    return apiRequest<BankInstitution[]>(`/api/bank/institutions?country=${encodeURIComponent(country)}`)
  }

  listConnections(): Promise<BankConnection[]> {
    return apiRequest<BankConnection[]>("/api/bank/connections")
  }

  // Returns the hosted bank-login link to redirect the user to. Banks are
  // identified by (name, country). `logo` is forwarded from the institutions
  // list the client already has so the server can skip a redundant /aspsps
  // fetch — keeping /connect fast enough that mobile Safari doesn't drop the
  // request. `reference` is echoed back as the redirect `state`; we stash it so
  // the startup guard recognises our callback.
  //
  // `origin` and `platform` are recorded on the connection because the bank's
  // callback is a separate public request with no client context — by then the
  // row is the only thing that knows whether to come back to the web app or the
  // native deep link, and whether this link was started during onboarding.
  connect(
    name: string,
    country: string,
    logo?: string | null,
    intent?: { origin?: BankOrigin; platform?: BankPlatform },
  ): Promise<{ connectionId: string; link: string; reference: string }> {
    // Path is "/authorize", not "/connect": mobile Safari content/ad blockers
    // block URLs ending in "/connect" (tracker filter rules), which killed this
    // request on-device with an instant "TypeError: Load failed". See the server
    // route comment in backend/internal/routes/bank/bank.go.
    return apiRequest<{ connectionId: string; link: string; reference: string }>("/api/bank/authorize", {
      method: "POST",
      body: {
        name,
        country,
        logo,
        origin: intent?.origin ?? "settings",
        platform: intent?.platform ?? (Capacitor.isNativePlatform() ? "native" : "web"),
      },
      // Bank authorisation involves a server-side call to the aggregator; bound
      // it so a hang fails as a clean timeout rather than Safari's "Load failed".
      timeoutMs: 30_000,
    })
  }

  // After connecting, the bank's accounts await selection. List the candidates…
  listConnectionAccounts(connectionId: string): Promise<BankCandidateAccount[]> {
    return apiRequest<BankCandidateAccount[]>(`/api/bank/connections/${connectionId}/accounts`, {
      // The server fans out to the aggregator for each account; bound the wait
      // so a hung request surfaces as an error (with a Retry) instead of the
      // picker showing "Loading your accounts…" forever.
      timeoutMs: 30_000,
    })
  }

  // …then import the chosen ones (with chosen names). Marks the link complete.
  importConnectionAccounts(connectionId: string, selected: BankAccountSelection[]): Promise<BankSyncResult> {
    return apiRequest<BankSyncResult>(`/api/bank/connections/${connectionId}/accounts`, {
      method: "POST",
      body: { selected },
      timeoutMs: 60_000,
    })
  }

  // Derive the onboarding review from what the import just brought in: income,
  // categories, subscriptions, and how much of it we could read. Only valid for
  // connections started from onboarding (409 otherwise), and idempotent — the
  // server caches the result on the connection, so a retry replays it rather
  // than recomputing.
  bootstrap(connectionId: string): Promise<BankBootstrapResult> {
    return apiRequest<BankBootstrapResult>(`/api/bank/connections/${connectionId}/bootstrap`, {
      method: "POST",
      // Reads the whole 90-day import and makes up to two model calls. Slower
      // than a normal request, so it gets the import's budget rather than the
      // default.
      timeoutMs: 60_000,
    })
  }

  // The user accepted the review: remember the merchant→category choices for
  // future syncs and back-fill the rows already imported. Any new categories
  // must exist before this is called — a mapping to a category that isn't there
  // is dropped on the floor.
  acceptBootstrap(
    connectionId: string,
    mappings: BankMerchantMapping[],
  ): Promise<{ learned: number; categorized: number }> {
    return apiRequest<{ learned: number; categorized: number }>(
      `/api/bank/connections/${connectionId}/bootstrap/accept`,
      { method: "POST", body: { mappings }, timeoutMs: 30_000 },
    )
  }

  // Exchange a bank ?code=&state= that landed on the app (Enable Banking
  // redirected to /settings instead of the server callback; the Supabase pkce
  // client would otherwise eat the code — see utils/bankCallbackCapture). Moves
  // the connection to "selecting" so the picker can open.
  exchangeCallback(
    code: string,
    state: string,
  ): Promise<{ connectionId: string; status: string; error?: string | null }> {
    return apiRequest<{ connectionId: string; status: string; error?: string | null }>("/api/bank/exchange", {
      method: "POST",
      body: { code, state },
      timeoutMs: 30_000,
    })
  }

  sync(connectionId: string): Promise<BankSyncResult> {
    return apiRequest<BankSyncResult>(`/api/bank/connections/${connectionId}/sync`, { method: "POST" })
  }

  // disconnect removes the link. With purge=true it also deletes the accounts
  // this bank created and the transactions imported from them (clean removal /
  // re-import). Default keeps the imported data.
  disconnect(connectionId: string, purge = false): Promise<void> {
    const qs = purge ? "?purge=true" : ""
    return apiRequest<void>(`/api/bank/connections/${connectionId}${qs}`, { method: "DELETE" })
  }
}

export const bankService = new BankService()

// Every /api/bank route 503s when the server has no Enable Banking
// credentials attached (`not_configured`). That is an outage on our side, not
// something the user can retry away — the UI says so rather than offering a
// "try again" that cannot succeed until the deployment is fixed.
//
// The bare 503 is matched too, not just the code: a client build ships
// independently of the backend, so it has to recognise the shape the currently
// deployed server sends (which predates the code). No other bank route
// produces a 503, so the widening costs nothing but covers a cold-start
// gateway 503 as unavailable — which is also the honest message there.
export function isBankNotConfigured(error: unknown): boolean {
  if (!error || typeof error !== "object") return false
  const { status, code } = error as { status?: number; code?: string }
  return code === "not_configured" || status === 503
}
